import React, { useState, useEffect } from 'react';
import { TacticsImage, TacticsImageStatus } from '../../../shared/services/tacticsImagesService';
import { scoresheetService } from '../../../shared/services/scoresheetService';
import DeleteTacticsModal from './DeleteTacticsModal';
import EditTacticsNameModal from './EditTacticsNameModal';

type MatchPlayer = { player_id: string; full_name: string; kit_number: number | null; team_id: string; team_name: string };

interface SavedTacticsImagesListProps {
  matchId: string;
  onLoadTactics?: (tacticsImage: TacticsImage) => void;
  onUploadTactics?: (tacticsImage: TacticsImage) => void;
  onDeleteTactics?: (tacticsImageId: string) => void;
  onEditTactics?: (tacticsImageId: string, newName: string) => void;
  refreshKey?: number;
}

const statusClasses: Record<TacticsImageStatus, string> = {
  saved: 'bg-gray-100 text-gray-700',
  uploaded: 'bg-green-100 text-green-700',
};

const SavedTacticsImagesList: React.FC<SavedTacticsImagesListProps> = ({
  matchId,
  onLoadTactics,
  onUploadTactics,
  onDeleteTactics,
  onEditTactics,
  refreshKey = 0,
}) => {
  const [tacticsImages, setTacticsImages] = useState<TacticsImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false); // Collapsed by default
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTacticsImage, setSelectedTacticsImage] = useState<TacticsImage | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [matchPlayers, setMatchPlayers] = useState<MatchPlayer[]>([]);

  const loadTacticsImages = async () => {
    if (!matchId) return;
    setLoading(true);
    setError(null);
    
    try {
      const { tacticsImagesService } = await import('../../../shared/services/tacticsImagesService');
      const result = await tacticsImagesService.getTacticsImagesByMatchId(matchId);
      
      if (result.success && result.data) {
        setTacticsImages(result.data);
      } else {
        setTacticsImages([]);
        setError(result.error || 'Failed to load tactics images');
      }
    } catch (err) {
      setTacticsImages([]);
      setError(err instanceof Error ? err.message : 'Failed to load tactics images');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTacticsImages();
  }, [matchId, refreshKey]);

  useEffect(() => {
    if (!matchId) return;
    let cancelled = false;
    scoresheetService.getMatchLineupPlayers(matchId).then((players) => {
      if (!cancelled) setMatchPlayers(players);
    }).catch(() => {
      if (!cancelled) setMatchPlayers([]);
    });
    return () => { cancelled = true; };
  }, [matchId]);

  const getPlayerNameForTactics = (tacticsImage: TacticsImage): string | null => {
    const config = tacticsImage.visualization_config;
    if (!config) return null;
    let playerId: string | undefined;
    if (typeof config === 'object' && config.filters && typeof config.filters === 'object') {
      playerId = (config.filters as { player_id?: string }).player_id;
    } else if (typeof config === 'string') {
      try {
        const parsed = JSON.parse(config) as { filters?: { player_id?: string } };
        playerId = parsed?.filters?.player_id;
      } catch {
        return null;
      }
    }
    if (!playerId) return null;
    const player = matchPlayers.find((p) => p.player_id === playerId);
    return player ? player.full_name : 'Unknown player';
  };

  const handleLoad = (tacticsImage: TacticsImage) => {
    onLoadTactics?.(tacticsImage);
  };

  const handleUpload = (e: React.MouseEvent, tacticsImage: TacticsImage) => {
    e.stopPropagation();
    onUploadTactics?.(tacticsImage);
  };

  const handleDeleteClick = (e: React.MouseEvent, tacticsImage: TacticsImage) => {
    e.stopPropagation();
    setSelectedTacticsImage(tacticsImage);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedTacticsImage) {
      onDeleteTactics?.(selectedTacticsImage.id);
      setIsDeleteModalOpen(false);
      setSelectedTacticsImage(null);
      // Reload list after deletion
      setTimeout(() => loadTacticsImages(), 500);
    }
  };

  const handleEditClick = (e: React.MouseEvent, tacticsImage: TacticsImage) => {
    e.stopPropagation();
    setSelectedTacticsImage(tacticsImage);
    setIsEditModalOpen(true);
  };

  const handleEditSave = async (newName: string) => {
    if (!selectedTacticsImage) return;
    
    setIsSaving(true);
    try {
      await onEditTactics?.(selectedTacticsImage.id, newName);
      setIsEditModalOpen(false);
      setSelectedTacticsImage(null);
      // Reload list after edit
      setTimeout(() => loadTacticsImages(), 500);
    } catch (error) {
      console.error('Error saving tactics name:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewImage = (e: React.MouseEvent, imageUrl: string) => {
    e.stopPropagation();
    window.open(imageUrl, '_blank');
  };

  // Show loading/error/empty states in collapsed header
  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <svg
              className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-sm font-medium text-gray-700">Saved Tactics Configurations</span>
          </div>
          <span className="text-xs text-gray-500">Loading...</span>
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-red-200 rounded-lg">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <svg
              className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-sm font-medium text-gray-700">Saved Tactics Configurations</span>
          </div>
          <span className="text-xs text-red-600">Error</span>
        </button>
        {isExpanded && (
          <div className="px-3 pb-3">
            <div className="text-sm text-red-600">{error}</div>
          </div>
        )}
      </div>
    );
  }

  if (tacticsImages.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <svg
              className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-sm font-medium text-gray-700">Saved Tactics Configurations</span>
          </div>
          <span className="text-xs text-gray-500">No saved configurations</span>
        </button>
        {isExpanded && (
          <div className="px-3 pb-3">
            <div className="text-sm text-gray-500">No saved tactics configurations yet.</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors rounded-t-lg"
      >
        <div className="flex items-center gap-2">
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-sm font-medium text-gray-700">Saved Tactics Configurations</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
            {tacticsImages.length}
          </span>
        </div>
      </button>

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-gray-100">
          {tacticsImages.map((tacticsImage) => (
            <div
              key={tacticsImage.id}
              className="bg-gray-50 border border-gray-200 rounded-lg p-3 hover:border-gray-300 hover:bg-gray-100 transition-colors cursor-pointer mt-2"
              onClick={() => handleLoad(tacticsImage)}
            >
          <div className="flex items-center justify-between">
            {/* Left Side - Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-base font-semibold text-gray-900 truncate">
                  {tacticsImage.name}
                </h3>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                    statusClasses[tacticsImage.status] || 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {tacticsImage.status.charAt(0).toUpperCase() + tacticsImage.status.slice(1)}
                </span>
              </div>
              {(() => {
                const playerName = getPlayerNameForTactics(tacticsImage);
                if (playerName) {
                  return (
                    <p className="text-xs text-gray-600 mb-1">
                      Players: {playerName}
                    </p>
                  );
                }
                return (
                  <p className="text-xs text-gray-500 mb-1">Team chart</p>
                );
              })()}
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span>{tacticsImage.view_types.join(', ')}</span>
                {tacticsImage.compare_mode && <span>• Compare mode</span>}
                <span>•</span>
                <span>Saved {new Date(tacticsImage.created_at).toLocaleDateString()}</span>
                {tacticsImage.file_size_bytes && (
                  <>
                    <span>•</span>
                    <span>{(tacticsImage.file_size_bytes / 1024).toFixed(1)} KB</span>
                  </>
                )}
              </div>
            </div>

            {/* Right Side - Actions */}
            <div
              className="flex items-center space-x-1 ml-4 flex-shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Upload Button - Only for saved tactics */}
              {tacticsImage.status === 'saved' && (
                <button
                  onClick={(e) => handleUpload(e, tacticsImage)}
                  className="p-1.5 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                  title="Upload to cloud"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </button>
              )}

              {/* View Image Button - Only for uploaded tactics */}
              {tacticsImage.status === 'uploaded' && tacticsImage.image_url && (
                <button
                  onClick={(e) => handleViewImage(e, tacticsImage.image_url!)}
                  className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  title="View image"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                </button>
              )}

              {/* Edit Button */}
              <button
                onClick={(e) => handleEditClick(e, tacticsImage)}
                className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                title="Edit name"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>

              {/* Delete Button */}
              <button
                onClick={(e) => handleDeleteClick(e, tacticsImage)}
                className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
                title="Delete"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteTacticsModal
        isOpen={isDeleteModalOpen}
        tacticsImageName={selectedTacticsImage?.name}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedTacticsImage(null);
        }}
        onConfirm={handleDeleteConfirm}
      />

      {/* Edit Name Modal */}
      <EditTacticsNameModal
        isOpen={isEditModalOpen}
        currentName={selectedTacticsImage?.name || ''}
        isSaving={isSaving}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedTacticsImage(null);
        }}
        onSave={handleEditSave}
      />
    </div>
  );
};

export default SavedTacticsImagesList;

